import { apiBaseUrl } from "../../client/src/main.ts";
import { toast } from "react-hot-toast";

const codeBlockStyle = {
  backgroundColor: "#1e1e1e",
  color: "#d4d4d4",
  padding: "16px",
  borderRadius: "8px",
  overflowX: "auto",
  fontFamily: "monospace",
  fontSize: "13px",
  lineHeight: "1.6",
};

const copyButtonStyle = {
  position: "absolute",
  top: "8px",
  right: "8px",
  padding: "6px 12px",
  backgroundColor: "#2d2d2d",
  color: "#d4d4d4",
  border: "1px solid #4d4d4d",
  borderRadius: "4px",
  cursor: "pointer",
  fontSize: "12px",
  fontFamily: "sans-serif",
  transition: "background-color 0.2s",
} as const;

const codeBlockContainerStyle = {
  position: "relative",
  marginBottom: "8px",
};

const sectionStyle = {
  marginBottom: "32px",
  padding: "20px",
  backgroundColor: "var(--surface-alt)",
  borderRadius: "var(--radius)",
  border: "1px solid var(--border)",
};

const headerStyle = {
  marginTop: 0,
  marginBottom: "8px",
  color: "#4fc3f7",
};

const badgeStyle = (color: string) => ({
  display: "inline-block",
  padding: "4px 8px",
  borderRadius: "4px",
  fontSize: "12px",
  fontWeight: 600,
  backgroundColor: color,
  color: "white",
  marginRight: "8px",
});

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text).then(
    () => {
      toast.success("Copied to clipboard!");
    },
    () => {
      toast.error("Failed to copy");
    },
  );
};

const CodeBlock = ({ code }: { code: string }) => (
  <div style={codeBlockContainerStyle}>
    <button
      type="button"
      style={copyButtonStyle}
      onClick={() =>
        copyToClipboard(code)}
      onMouseEnter={(e) => {
        (e.target as HTMLButtonElement).style.backgroundColor = "#3d3d3d";
      }}
      onMouseLeave={(e) => {
        (e.target as HTMLButtonElement).style.backgroundColor = "#2d2d2d";
      }}
    >
      Copy
    </button>
    <pre style={codeBlockStyle}>{code}</pre>
  </div>
);

const EndpointSection = (
  {
    title,
    method,
    endpoint,
    description,
    auth,
    requestBody,
    responseBody,
    example,
  }: {
    title: string;
    method: "POST";
    endpoint: string;
    description: string;
    auth: string;
    requestBody: Record<
      string,
      { type: string; required?: boolean; description: string }
    >;
    responseBody: Record<string, { type: string; description: string }>;
    example: { request: unknown; response: unknown };
  },
) => (
  <div style={sectionStyle}>
    <h3 style={headerStyle}>{title}</h3>
    <div style={{ marginBottom: "16px" }}>
      <span style={badgeStyle("#2e7d32")}>{method}</span>
      <code style={{ fontSize: "14px" }}>{apiBaseUrl}{endpoint}</code>
    </div>
    <p style={{ marginBottom: "16px", opacity: 0.9 }}>{description}</p>

    <div style={{ marginBottom: "16px" }}>
      <strong>Authentication:</strong> {auth}
    </div>

    <div style={{ marginBottom: "16px" }}>
      <strong>Request Body:</strong>
      <div style={{ marginTop: "8px" }}>
        {Object.entries(requestBody).map(([key, value]) => (
          <div key={key} style={{ marginLeft: "16px", marginBottom: "8px" }}>
            <code style={{ color: "#ce9178" }}>{key}</code>{" "}
            <span style={{ opacity: 0.7 }}>({value.type})</span>
            {value.required && (
              <span style={{ color: "#f48771" }}>*required</span>
            )}
            <div style={{ marginLeft: "16px", fontSize: "13px", opacity: 0.8 }}>
              {value.description}
            </div>
          </div>
        ))}
      </div>
    </div>

    <div style={{ marginBottom: "16px" }}>
      <strong>Response:</strong>
      <div style={{ marginTop: "8px" }}>
        {Object.entries(responseBody).map(([key, value]) => (
          <div key={key} style={{ marginLeft: "16px", marginBottom: "8px" }}>
            <code style={{ color: "#ce9178" }}>{key}</code>{" "}
            <span style={{ opacity: 0.7 }}>({value.type})</span>
            <div style={{ marginLeft: "16px", fontSize: "13px", opacity: 0.8 }}>
              {value.description}
            </div>
          </div>
        ))}
      </div>
    </div>

    <div>
      <strong>Example:</strong>
      <div style={{ marginTop: "8px" }}>
        <div style={{ marginBottom: "8px", fontSize: "13px", opacity: 0.8 }}>
          Request:
        </div>
        <CodeBlock code={JSON.stringify(example.request, null, 2)} />
      </div>
      <div style={{ marginTop: "12px" }}>
        <div style={{ marginBottom: "8px", fontSize: "13px", opacity: 0.8 }}>
          Response:
        </div>
        <CodeBlock code={JSON.stringify(example.response, null, 2)} />
      </div>
    </div>
  </div>
);

export const ApiDocs = () => (
  <div style={{ padding: "20px", maxWidth: "1000px", margin: "0 auto" }}>
    <h1 style={{ marginTop: 0, marginBottom: "8px" }}>API Documentation</h1>
    <p style={{ opacity: 0.85, marginBottom: "32px" }}>
      Integrate Supergreen WhatsApp automation into your application using our
      HTTP API.
    </p>

    <div style={sectionStyle}>
      <h2 style={{ marginTop: 0, marginBottom: "16px" }}>Getting Started</h2>
      <p>
        All API endpoints require authentication using your account's secret
        token. You can find this token in your WhatsApp account settings on the
        main dashboard.
      </p>
      <p style={{ marginTop: "12px" }}>
        <strong>Base URL:</strong> <code>{apiBaseUrl}</code>
      </p>
      <p style={{ marginTop: "12px" }}>
        All endpoints accept JSON request bodies and return JSON responses. Make
        sure to set the <code>Content-Type: application/json</code> header.
      </p>
    </div>

    <div style={sectionStyle}>
      <h2 style={{ marginTop: 0, marginBottom: "16px" }}>
        Node.js Quick Start
      </h2>
      <CodeBlock
        code={`// Install the client (optional)
// npm install node-fetch

const fetch = require('node-fetch');

const API_BASE = '${apiBaseUrl}';
const YOUR_TOKEN = 'your-secret-token-here';
const YOUR_PHONE = 'your-whatsapp-number';

async function sendMessage(toNumber, message) {
  const response = await fetch(API_BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      endpoint: 'sendMessage',
      payload: {
        fromNumber: YOUR_PHONE,
        toNumber: toNumber,
        message: message,
        linkPreview: false,
        token: YOUR_TOKEN
      }
    })
  });
  return response.json();
}

// Example usage
sendMessage('972501234567@c.us', 'Hello from Node.js!')
  .then(result => console.log('Message sent:', result))
  .catch(err => console.error('Error:', err));`}
      />
    </div>

    <h2 style={{ marginTop: "32px", marginBottom: "16px" }}>
      Messaging Endpoints
    </h2>

    <EndpointSection
      title="Send Text Message"
      method="POST"
      endpoint="/sendMessage"
      description="Send a text message to any WhatsApp number or group."
      auth="Required - Include token in request body"
      requestBody={{
        fromNumber: {
          type: "string",
          required: true,
          description: "Your WhatsApp account phone number",
        },
        toNumber: {
          type: "string",
          required: true,
          description:
            "Recipient's WhatsApp ID (e.g., '972501234567@c.us' for individuals or 'group-id@g.us' for groups)",
        },
        message: {
          type: "string",
          required: true,
          description: "The text message to send",
        },
        linkPreview: {
          type: "boolean",
          required: true,
          description: "Whether to show link previews for URLs in the message",
        },
        token: {
          type: "string",
          required: true,
          description: "Your account's secret authentication token",
        },
      }}
      responseBody={{
        messageId: {
          type: "string",
          description: "Unique identifier for the sent message",
        },
      }}
      example={{
        request: {
          fromNumber: "972501234567",
          toNumber: "972509876543@c.us",
          message: "Hello! This is a test message.",
          linkPreview: false,
          token: "your-secret-token",
        },
        response: {
          messageId: "3EB0123456789ABCDEF",
        },
      }}
    />

    <EndpointSection
      title="Send Media"
      method="POST"
      endpoint="/sendMedia"
      description="Send images, videos, or documents with optional caption to any WhatsApp number or group."
      auth="Required - Include token in request body"
      requestBody={{
        fromNumber: {
          type: "string",
          required: true,
          description: "Your WhatsApp account phone number",
        },
        toNumber: {
          type: "string",
          required: true,
          description: "Recipient's WhatsApp ID",
        },
        base64: {
          type: "string",
          required: true,
          description:
            "Base64 encoded media data (with or without data URL prefix)",
        },
        type: {
          type: "string",
          required: true,
          description: "Media type: 'image', 'video', or 'document'",
        },
        mimeType: {
          type: "string",
          description:
            "Media MIME type (e.g., 'image/jpeg', 'video/mp4', 'application/pdf'). Auto-detected from data URL if not provided",
        },
        filename: {
          type: "string",
          description:
            "Filename for the media. Auto-generated from MIME type if not provided",
        },
        caption: {
          type: "string",
          description: "Optional caption text for the media",
        },
        token: {
          type: "string",
          required: true,
          description: "Your account's secret authentication token",
        },
      }}
      responseBody={{
        messageId: {
          type: "string",
          description: "Unique identifier for the sent media message",
        },
      }}
      example={{
        request: {
          fromNumber: "972501234567",
          toNumber: "972509876543@c.us",
          base64:
            "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==",
          type: "image",
          caption: "Check out this image!",
          token: "your-secret-token",
        },
        response: {
          messageId: "3EB0987654321FEDCBA",
        },
      }}
    />

    <h2 style={{ marginTop: "32px", marginBottom: "16px" }}>
      Chat Interaction
    </h2>

    <EndpointSection
      title="Send Typing Indication"
      method="POST"
      endpoint="/sendTypingIndication"
      description="Show the 'typing...' indicator in a chat to make your bot feel more human."
      auth="Required - Include token in request body"
      requestBody={{
        fromNumber: {
          type: "string",
          required: true,
          description: "Your WhatsApp account phone number",
        },
        chatId: {
          type: "string",
          required: true,
          description: "Chat ID where typing indicator should appear",
        },
        durationMs: {
          type: "number",
          description:
            "Duration in milliseconds (optional, defaults to standard duration)",
        },
        token: {
          type: "string",
          required: true,
          description: "Your account's secret authentication token",
        },
      }}
      responseBody={{
        success: {
          type: "boolean",
          description:
            "Whether the typing indication was successfully triggered",
        },
      }}
      example={{
        request: {
          fromNumber: "972501234567",
          chatId: "972509876543@c.us",
          durationMs: 3000,
          token: "your-secret-token",
        },
        response: {
          success: true,
        },
      }}
    />

    <EndpointSection
      title="Stop Typing Indication"
      method="POST"
      endpoint="/stopTypingIndication"
      description="Stop showing the 'typing...' indicator in a chat."
      auth="Required - Include token in request body"
      requestBody={{
        fromNumber: {
          type: "string",
          required: true,
          description: "Your WhatsApp account phone number",
        },
        chatId: {
          type: "string",
          required: true,
          description: "Chat ID where typing indicator should stop",
        },
        token: {
          type: "string",
          required: true,
          description: "Your account's secret authentication token",
        },
      }}
      responseBody={{
        success: {
          type: "boolean",
          description: "Whether the typing indication was successfully stopped",
        },
      }}
      example={{
        request: {
          fromNumber: "972501234567",
          chatId: "972509876543@c.us",
          token: "your-secret-token",
        },
        response: {
          success: true,
        },
      }}
    />

    <h2 style={{ marginTop: "32px", marginBottom: "16px" }}>
      Group Management
    </h2>

    <EndpointSection
      title="Get Admin Groups"
      method="POST"
      endpoint="/getAdminWhatsappGroups"
      description="Retrieve a list of all WhatsApp groups where your account is an admin."
      auth="Required - Include token in request body"
      requestBody={{
        fromNumber: {
          type: "string",
          required: true,
          description: "Your WhatsApp account phone number",
        },
        token: {
          type: "string",
          required: true,
          description: "Your account's secret authentication token",
        },
      }}
      responseBody={{
        groups: {
          type: "array",
          description: "Array of group objects with id and name",
        },
      }}
      example={{
        request: {
          fromNumber: "972501234567",
          token: "your-secret-token",
        },
        response: {
          groups: [
            { id: "120363123456789012@g.us", name: "My Cool Group" },
            { id: "120363987654321098@g.us", name: "Another Group" },
          ],
        },
      }}
    />

    <EndpointSection
      title="Add User to Group"
      method="POST"
      endpoint="/addUserToWhatsAppGroup"
      description="Add a user to a WhatsApp group where you are an admin."
      auth="Required - Include token in request body"
      requestBody={{
        fromNumber: {
          type: "string",
          required: true,
          description: "Your WhatsApp account phone number",
        },
        groupId: {
          type: "string",
          required: true,
          description: "Group ID (e.g., '120363123456789012@g.us')",
        },
        userId: {
          type: "string",
          required: true,
          description: "User ID to add (e.g., '972509876543@c.us')",
        },
        token: {
          type: "string",
          required: true,
          description: "Your account's secret authentication token",
        },
      }}
      responseBody={{
        success: {
          type: "boolean",
          description: "Whether the user was successfully added",
        },
      }}
      example={{
        request: {
          fromNumber: "972501234567",
          groupId: "120363123456789012@g.us",
          userId: "972509876543@c.us",
          token: "your-secret-token",
        },
        response: {
          success: true,
        },
      }}
    />

    <EndpointSection
      title="Get Common Groups"
      method="POST"
      endpoint="/getCommonWhatsappGroups"
      description="Get a list of WhatsApp groups that both you and another user are members of."
      auth="Required - Include token in request body"
      requestBody={{
        fromNumber: {
          type: "string",
          required: true,
          description: "Your WhatsApp account phone number",
        },
        userId: {
          type: "string",
          required: true,
          description: "User ID to check common groups with",
        },
        token: {
          type: "string",
          required: true,
          description: "Your account's secret authentication token",
        },
      }}
      responseBody={{
        groups: {
          type: "array",
          description: "Array of common group objects with id and name",
        },
      }}
      example={{
        request: {
          fromNumber: "972501234567",
          userId: "972509876543@c.us",
          token: "your-secret-token",
        },
        response: {
          groups: [
            { id: "120363123456789012@g.us", name: "Shared Group" },
          ],
        },
      }}
    />

    <EndpointSection
      title="Get Join Requests"
      method="POST"
      endpoint="/getWhatsappJoinRequests"
      description="Get pending join requests for a group where you are an admin."
      auth="Required - Include token in request body"
      requestBody={{
        fromNumber: {
          type: "string",
          required: true,
          description: "Your WhatsApp account phone number",
        },
        groupId: {
          type: "string",
          required: true,
          description: "Group ID to check for join requests",
        },
        token: {
          type: "string",
          required: true,
          description: "Your account's secret authentication token",
        },
      }}
      responseBody={{
        requests: {
          type: "array",
          description: "Array of join request objects",
        },
      }}
      example={{
        request: {
          fromNumber: "972501234567",
          groupId: "120363123456789012@g.us",
          token: "your-secret-token",
        },
        response: {
          requests: [
            {
              requesterId: "972509876543@c.us",
              addedById: null,
              parentGroupId: null,
              method: "non_admin_add",
              timestamp: 1703251200000,
            },
          ],
        },
      }}
    />

    <EndpointSection
      title="Approve Join Requests"
      method="POST"
      endpoint="/approveWhatsappJoinRequests"
      description="Approve one or more pending join requests for a group."
      auth="Required - Include token in request body"
      requestBody={{
        fromNumber: {
          type: "string",
          required: true,
          description: "Your WhatsApp account phone number",
        },
        groupId: {
          type: "string",
          required: true,
          description: "Group ID where requests should be approved",
        },
        requesterIds: {
          type: "array | null",
          description:
            "Array of requester IDs to approve, or null to approve all",
        },
        token: {
          type: "string",
          required: true,
          description: "Your account's secret authentication token",
        },
      }}
      responseBody={{
        results: {
          type: "array",
          description: "Array of result objects for each requester",
        },
      }}
      example={{
        request: {
          fromNumber: "972501234567",
          groupId: "120363123456789012@g.us",
          requesterIds: ["972509876543@c.us"],
          token: "your-secret-token",
        },
        response: {
          results: [
            {
              requesterId: "972509876543@c.us",
              error: null,
              message: "Approved successfully",
            },
          ],
        },
      }}
    />

    <EndpointSection
      title="Reject Join Requests"
      method="POST"
      endpoint="/rejectWhatsappJoinRequests"
      description="Reject one or more pending join requests for a group."
      auth="Required - Include token in request body"
      requestBody={{
        fromNumber: {
          type: "string",
          required: true,
          description: "Your WhatsApp account phone number",
        },
        groupId: {
          type: "string",
          required: true,
          description: "Group ID where requests should be rejected",
        },
        requesterIds: {
          type: "array | null",
          description:
            "Array of requester IDs to reject, or null to reject all",
        },
        token: {
          type: "string",
          required: true,
          description: "Your account's secret authentication token",
        },
      }}
      responseBody={{
        results: {
          type: "array",
          description: "Array of result objects for each requester",
        },
      }}
      example={{
        request: {
          fromNumber: "972501234567",
          groupId: "120363123456789012@g.us",
          requesterIds: ["972509876543@c.us"],
          token: "your-secret-token",
        },
        response: {
          results: [
            {
              requesterId: "972509876543@c.us",
              error: null,
              message: "Rejected successfully",
            },
          ],
        },
      }}
    />

    <h2 style={{ marginTop: "32px", marginBottom: "16px" }}>
      Account Management
    </h2>

    <EndpointSection
      title="Set Webhook"
      method="POST"
      endpoint="/setWebhook"
      description="Set or update the webhook URL to receive incoming messages and events."
      auth="Required - Include token in request body"
      requestBody={{
        phoneNumber: {
          type: "string",
          required: true,
          description: "Your WhatsApp account phone number",
        },
        url: {
          type: "string | null",
          required: true,
          description:
            "Webhook URL to receive updates, or null to remove webhook",
        },
        token: {
          type: "string",
          required: true,
          description: "Your account's secret authentication token",
        },
      }}
      responseBody={{}}
      example={{
        request: {
          phoneNumber: "972501234567",
          url: "https://your-server.com/webhook",
          token: "your-secret-token",
        },
        response: {},
      }}
    />

    <EndpointSection
      title="Reconnect"
      method="POST"
      endpoint="/reconnect"
      description="Manually trigger a reconnection of your WhatsApp account."
      auth="Required - Include token in request body"
      requestBody={{
        number: {
          type: "string",
          required: true,
          description: "Your WhatsApp account phone number",
        },
        token: {
          type: "string",
          required: true,
          description: "Your account's secret authentication token",
        },
      }}
      responseBody={{}}
      example={{
        request: {
          number: "972501234567",
          token: "your-secret-token",
        },
        response: {},
      }}
    />

    <EndpointSection
      title="Trigger Code Login"
      method="POST"
      endpoint="/triggerCodeLogin"
      description="Request a pairing code for linking your WhatsApp account."
      auth="Required - Include token in request body"
      requestBody={{
        number: {
          type: "string",
          required: true,
          description: "Your WhatsApp account phone number",
        },
        token: {
          type: "string",
          required: true,
          description: "Your account's secret authentication token",
        },
      }}
      responseBody={{}}
      example={{
        request: {
          number: "972501234567",
          token: "your-secret-token",
        },
        response: {},
      }}
    />

    <EndpointSection
      title="Clear Credentials"
      method="POST"
      endpoint="/clearCredentials"
      description="Clear all stored credentials for your WhatsApp account. Use this to reset authentication."
      auth="Required - Include token in request body"
      requestBody={{
        phoneNumber: {
          type: "string",
          required: true,
          description: "Your WhatsApp account phone number",
        },
        token: {
          type: "string",
          required: true,
          description: "Your account's secret authentication token",
        },
      }}
      responseBody={{
        success: {
          type: "boolean",
          description: "Whether credentials were successfully cleared",
        },
        error: {
          type: "string",
          description: "Error message if operation failed",
        },
      }}
      example={{
        request: {
          phoneNumber: "972501234567",
          token: "your-secret-token",
        },
        response: {
          success: true,
        },
      }}
    />

    <div style={sectionStyle}>
      <h2 style={{ marginTop: 0, marginBottom: "16px" }}>Webhook Events</h2>
      <p>
        When you configure a webhook URL, Supergreen will send POST requests to
        your server whenever messages or events are received. The webhook
        payload follows this structure:
      </p>
      <CodeBlock
        code={JSON.stringify(
          {
            network: "whatsapp",
            chat: {
              id: "972509876543@c.us",
              username: "John Doe",
              title: "Optional Group Title",
            },
            author: {
              id: "972509876543@c.us",
              username: "John Doe",
            },
            text: "Hello! This is a message from a user.",
            time: 1703251200000,
          },
          null,
          2,
        )}
      />
    </div>

    <div style={sectionStyle}>
      <h2 style={{ marginTop: 0, marginBottom: "16px" }}>Error Handling</h2>
      <p>
        All API endpoints return standard HTTP status codes. Successful requests
        return 200 OK. Errors return appropriate status codes with JSON error
        details:
      </p>
      <CodeBlock
        code={JSON.stringify(
          {
            error: "Authentication failed",
            message: "Invalid token provided",
          },
          null,
          2,
        )}
      />
      <p style={{ marginTop: "12px" }}>Common error codes:</p>
      <ul style={{ marginLeft: "20px", opacity: 0.9 }}>
        <li>400 - Bad Request (invalid parameters)</li>
        <li>401 - Unauthorized (invalid token)</li>
        <li>404 - Not Found (endpoint doesn't exist)</li>
        <li>500 - Internal Server Error</li>
      </ul>
    </div>
  </div>
);
